/**
 * 锁定参数提示框组件
 * 点击锁头显示个性化计算说明
 * Requirements: 5.4
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Lock, Calculator, Sparkles } from "lucide-react";
import type { LockedParamTooltipProps } from "@/types/conversion-funnel";

/**
 * 锁定参数提示框
 * 点击锁头图标时显示，解释参数是如何计算的
 */
export function LockedParamTooltip({
  lockReason,
  tankVolume,
  children,
}: LockedParamTooltipProps) {
  const { t } = useTranslation("assessments");

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] p-3">
          <div className="space-y-2">
            {/* 标题 */}
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-amber-500 dark:text-amber-400" />
              <span className="font-medium text-foreground">
                {t("conversion.lockedParam.title", "个性化参数")}
              </span>
            </div>

            {/* 锁定原因 */}
            <p className="text-sm text-muted-foreground">{lockReason}</p>

            {/* 鱼缸体积信息 */}
            {tankVolume && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calculator className="h-3 w-3" />
                <span>
                  {t(
                    "conversion.lockedParam.tankVolume",
                    "基于您的 {{volume}}L 水体",
                    {
                      volume: tankVolume,
                    }
                  )}
                </span>
              </div>
            )}

            {/* 解锁提示 */}
            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Sparkles className="h-3 w-3 text-amber-500 dark:text-amber-400" />
              <span className="text-xs text-amber-600 dark:text-amber-400">
                {t("conversion.lockedParam.unlockHint", "解锁后查看精准数值")}
              </span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * 锁定参数显示组件
 * 显示锁头图标和单位，点击显示提示
 */
interface LockedParamDisplayProps {
  unit: string;
  lockReason: string;
  tankVolume?: number;
  className?: string;
}

export function LockedParamDisplay({
  unit,
  lockReason,
  tankVolume,
  className,
}: LockedParamDisplayProps) {
  const { t } = useTranslation("assessments");

  return (
    <LockedParamTooltip lockReason={lockReason} tankVolume={tankVolume}>
      <button
        className={cn(
          "inline-flex items-center gap-1 px-2 py-1",
          "bg-amber-100/50 text-amber-700 rounded-md",
          "dark:bg-amber-900/30 dark:text-amber-400",
          "hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors",
          "cursor-pointer",
          className
        )}
      >
        <Lock className="h-3 w-3" />
        <span className="text-sm font-medium">
          {t("conversion.lockedParam.locked", "🔒")} {unit}
        </span>
      </button>
    </LockedParamTooltip>
  );
}

export default LockedParamTooltip;
